import React, { memo, useCallback, useRef } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import { DateTime } from 'luxon';


const formatDateTime = (timeString) => {
    if (!timeString) return null;
    return DateTime.fromISO(timeString).toLocaleString(DateTime.DATETIME_MED);
};

const PersonRow = memo(({ p, isMain, onCheckIn, onCheckOut, mainPerson }) => {
    const isCheckedIn = p.isSelected;
    const isCheckedOut = p.isCheckedOut;
    const isProcessing = p.isProcessing;

    return (
        <View style={styles.personRow}>
            <View style={styles.personInfo}>
                <Avatar borderGap disableBorder size={40} profile={p} />
                <View style={styles.nameContainer}>
                    <View style={{flexDirection:'row', alignItems:'center', gap:3}}>
                        <Text style={styles.personName}>{p.firstName} {p.lastName}</Text>
                        {isCheckedOut && (
                            <Unicon name="exit" size={14} colorVariant="success" style={{marginBottom:3}} />
                        )}
                        <Unicon
                            name="checkmark-circle"
                            size={14}
                            colorVariant={isCheckedIn ? 'success' : 'textSecondary'}
                            style={{marginBottom:3, opacity:!isCheckedIn ? 0 : 1}}
                        />
                    </View>
                    {p.familyRole && (
                        <View style={styles.detailRow}>
                            <Unicon name="people-outline" size={12} colorVariant="text" />
                            <Text style={styles.detailTextFamilyRole}>{p.familyRole}</Text>
                        </View>
                    )}
                    {isCheckedIn && p.checkInLocation && (
                        <View style={styles.detailRow}>
                            <Unicon name="location-outline" size={12} colorVariant="textSecondary" />
                            <Text style={styles.detailText}>{p.checkInLocation}</Text>
                        </View>
                    )}
                    {isCheckedIn && p.checkInTime && (
                        <View style={styles.detailRow}>
                            <Unicon name="time-outline" size={12} colorVariant="textSecondary" />
                            <Text style={styles.detailText}>{formatDateTime(p.checkInTime)}</Text>
                        </View>
                    )}
                    {isCheckedIn && p.submitterName && (
                        <View style={styles.detailRow}>
                            <Unicon name="person-outline" size={12} colorVariant="textSecondary" />
                            <Text style={styles.detailText}>{p.submitterName}</Text>
                        </View>
                    )}
                </View>
            </View>
            <View style={styles.actionButtons}>
                {isCheckedIn && !isCheckedOut ? (
                    <>
                        <SolidButton
                            size="sm"
                            type="success"
                            text={isProcessing ? '...' : 'Check Out'}
                            onPress={() => onCheckOut?.(p, isMain ? null : mainPerson)}
                            disabled={isProcessing}
                            processing={isProcessing}
                            width={95}
                        />
                        <SolidButton
                            size="sm"
                            type="warning"
                            text={isProcessing ? '...' : 'Remove'}
                            onPress={() => onCheckIn?.(p, isCheckedIn, isCheckedOut, isMain ? null : mainPerson)}
                            disabled={isProcessing}
                            processing={isProcessing}
                            width={80}
                        />
                    </>
                ) : (
                    <SolidButton
                        size="sm"
                        type={isCheckedIn ? 'warning' : 'success'}
                        text={isProcessing ? '...' : (isCheckedIn ? 'Remove' : 'Check In')}
                        onPress={() => onCheckIn?.(p, isCheckedIn, isCheckedOut, isMain ? null : mainPerson)}
                        disabled={isProcessing}
                        processing={isProcessing}
                        width={95}
                    />
                )}
            </View>
        </View>
    );
});

const CheckinDetailsModal = ({ isOpen, onClose, person, onCheckIn, onCheckOut }) => {
        const onCheckInRef = useRef(onCheckIn);
    onCheckInRef.current = onCheckIn;

    const onCheckOutRef = useRef(onCheckOut);
    onCheckOutRef.current = onCheckOut;

    const stableOnCheckIn = useCallback((...args) => {
        onCheckInRef.current?.(...args);
    }, []);

    const stableOnCheckOut = useCallback((...args) => {
        onCheckOutRef.current?.(...args);
    }, []);

    if (!person) return null;

    const familyMembers = person?.Family?.FamilyMembers?.filter(p => p.id !== person?.id) || [];
    const otherHouseholdMembers = person?.Address?.People?.filter(p => p.id !== person?.id && !familyMembers.some(fm => fm.id === p.id)) || [];
    const hasFamily = familyMembers.length > 0 || otherHouseholdMembers.length > 0;

    return (
        <SheetModal isOpen={isOpen} onCancel={onClose} snapPoints={["85%"]} title="Check-in Details">
            <ScrollView showsVerticalScrollIndicator={false}keyboardShouldPersistTaps="handled"
>
                {/* Main Person */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Main Person</Text>
                    <PersonRow p={person} isMain={true} onCheckIn={stableOnCheckIn} onCheckOut={stableOnCheckOut} mainPerson={person} />
                </View>

                {/* Family */}
                {familyMembers.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Family</Text>
                        {familyMembers.map(member => (
                            <PersonRow key={member.id} p={member} isMain={false} onCheckIn={stableOnCheckIn} onCheckOut={stableOnCheckOut} mainPerson={person} />
                        ))}
                    </View>
                )}

                {/* Other Household Members */}
                {otherHouseholdMembers.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Other Household Members</Text>
                        {otherHouseholdMembers.map(member => (
                            <PersonRow key={member.id} p={member} isMain={false} onCheckIn={stableOnCheckIn} onCheckOut={stableOnCheckOut} mainPerson={person} />
                        ))}
                    </View>
                )}

                {!hasFamily && (
                    <View style={styles.noFamilyContainer}>
                        <Text style={styles.noFamilyText}>No family members found</Text>
                    </View>
                )}
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    section: {
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 12,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    personRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
        borderRadius: 8,
        marginBottom: 8,
    },
    actionButtons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    personInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
    },
    nameContainer: {
        marginLeft: 12,
        flex: 1,
        minWidth: 0,
    },
    personName: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 4,
    },
    statusRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginBottom: 2,
    },
    statusText: (color) => ({
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors[color] || colors.textSecondary,
    }),
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 2,
    },
    detailTextFamilyRole: {
        fontSize: 11,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    detailText: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    noFamilyContainer: {
        paddingVertical: 30,
        alignItems: 'center',
    },
    noFamilyText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
}));

export default CheckinDetailsModal;
